import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useContext, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { transformListData } from '_lib/api/apiResourceFactory';
import {
  activeCallOutcomes,
  CallOutcomeEnum,
  TimezoneAbbreviationEnum,
  completionStatuses,
  SipTransferIntentMap,
  AiCallAgentEnum,
  AiCallAgentDisplayMap,
  NonEmptyArray,
  SipTransferIntentDisplayMap,
  completionStatusDisplay,
  arrayOfAll
} from '_lib/api/apiTypes.shim';
import { AiCallQueryParamsWithUnknownOutcome } from '_lib/api/resources/aiCallResource';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { DebouncedTextField } from '_lib/components/DebouncedTextField/DebouncedTextField';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { GridV2 } from '_lib/components/GridV2/GridV2';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MaskedPhoneInput } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { DatePicker } from '_lib/components/Pickers/DatePicker/DatePicker';
import { SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';

import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { parsePhone10 } from '_lib/utils/helpers';

import { SimpleAppBar } from '../../components/AppBar/SimpleAppBar';
import { AppDrawerState, AppRouteContext } from '../App/app.types';

import { AuthContext } from '../auth/authContext';
import { CallCenterUser, SystemUser } from '../auth/permissionHelpers';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { PagedList } from '../PagedList/PagedList';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';
import { SearchSelect } from '../SearchSelect/SearchSelect';

import { AiCallIcon } from './AiCallIcon';

type AiCallListProps = {
  appDrawerState?: AppDrawerState;
  timezone: TimezoneAbbreviationEnum;
};

enum DateRangeEnum {
  Today = 'Today',
  WeekToDate = 'Week to Date',
  MonthToDate = 'Month to Date',
  YearToDate = 'Year to Date',
  CustomRange = 'Custom Range'
}
export const dateRangeOptions = Object.values(DateRangeEnum);
type DateRangeFilter = (typeof dateRangeOptions)[number];
export const isDateRangeOption = (val: string): val is DateRangeFilter => dateRangeOptions.some((s) => s === val);

const AiCallList = (props: AiCallListProps) => {
  const { appDrawerState, timezone } = props;

  const navigate = useNavigate();

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const api = useContext(ApiContext);

  const { authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);

  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const { isAccountUser } = useAuthRoles();

  const companyNameQuery = api.companies.useUnpagedList({
    params: { serializer: 'name', accountId: cachedAuthUserQuery.data?.accountId || undefined }
  });

  const { params, setParam, setParams, deleteParam, clearParams, searchParams } = useQueryParams({
    outcome: {
      defaultValue: undefined,
      normalize: normalizers.enum(
        arrayOfAll<CallOutcomeEnum | 'UNKNOWN'>()(['UNKNOWN', ...Object.values(CallOutcomeEnum).sort()])
      )
    },
    agent: { defaultValue: undefined, normalize: normalizers.enum(Object.values(AiCallAgentEnum)) },
    accountId: { defaultValue: '' },
    companyId: { defaultValue: '' },
    companyDNIS: { defaultValue: '' },
    completionStatus: { defaultValue: undefined, normalize: normalizers.enum(completionStatuses()) },
    sipTransferIntent: { defaultValue: undefined, normalize: normalizers.enum(Object.values(SipTransferIntentMap)) },
    companySwitched: { defaultValue: false, normalize: normalizers.boolean },
    dateRangeFilter: { defaultValue: DateRangeEnum.Today, normalize: normalizers.enum(dateRangeOptions) },
    createdAt_gte: { defaultValue: '', normalize: normalizers.isoDate },
    createdAt_lte: { defaultValue: '', normalize: normalizers.isoDate },
    callerId: { defaultValue: '', normalize: (value) => parsePhone10(value) || '' }
  });

  const companyQuery = api.companies.useGetById(params.companyId ? { id: params.companyId } : { skipQuery: true });
  const accountQuery = api.accounts.useGetById(params.accountId ? { id: params.accountId } : { skipQuery: true });

  /**
   * Ignore 'companyDNIS' param if not in current company data
   */
  const sanitizedCompanyDNIS =
    params.companyDNIS &&
    !companyQuery.data?.callCenter.dnisArray.find((dnisItem) => dnisItem.phoneNumber === params.companyDNIS)
      ? ''
      : params.companyDNIS;
  const now = new Date();

  const createdAt_gte_Today = DateUtils.toStartOf(now, 'day', { timezone }).toISOString();
  const createdAt_gte_Week = DateUtils.toStartOf(now, 'week', { timezone }).toISOString();
  const createdAt_gte_Month = DateUtils.toStartOf(now, 'month', { timezone }).toISOString();
  const createdAt_gte_Year = DateUtils.toStartOf(now, 'year', { timezone }).toISOString();

  const createdAt_lte_Today = DateUtils.toEndOf(now, 'day', { timezone }).toISOString();

  const aiCallParams: AiCallQueryParamsWithUnknownOutcome = {
    callCenterId: cachedCallCenterQuery.data?.id,
    outcome: params.outcome,
    agent: params.agent,
    accountId: cachedAuthUserQuery?.data?.accountId ?? accountQuery.data?.id,
    companyId: companyQuery.data?.id,
    companyDNIS: sanitizedCompanyDNIS,
    completionStatus: params.completionStatus,
    sipTransferIntent: params.sipTransferIntent,
    companySwitched: String(params.companySwitched),
    createdAt_gte: params.createdAt_gte || createdAt_gte_Today,
    createdAt_lte: params.createdAt_lte || createdAt_lte_Today,
    callerId: params.callerId
  };

  const enableAiCallQueries = !!cachedAuthUserQuery?.data && !companyQuery.isFetching && !accountQuery.isFetching;

  const aiCallQuery = api.aiCalls.useList(
    {
      params: aiCallParams
    },
    { enabled: enableAiCallQueries }
  );

  const aiCallCountQuery = api.aiCalls.useCount({ params: aiCallParams }, { enabled: enableAiCallQueries });

  const [callerIdIsPartiallyFilled, setCallerIdIsPartiallyFilled] = useState(false);

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='CPI Calls' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <Divider />
      <GridV2
        container
        spacing={1}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, padding: 1.5 })}>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Call Outcome'
            initialKey={params.outcome}
            options={['UNKNOWN', ...activeCallOutcomes()]}
            enableEmptyValue
            onChange={(outcome) => setParam('outcome', outcome)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='AI Agent'
            initialKey={params.agent}
            options={
              Object.entries(AiCallAgentDisplayMap).map(([key, display]) => [key, display]) as NonEmptyArray<
                [string, string]
              >
            }
            enableEmptyValue
            onChange={(agent) => setParam('agent', agent)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SearchSelect
            doSearch={async (searchValue) => {
              const res = await api.companies.list({ params: { searchWords: searchValue } });
              return res.results;
            }}
            selectedOption={companyQuery?.data || null}
            trySetSelectedOption={async (option) => {
              if (option) {
                setParams({ companyId: option.id, companyDNIS: null });
                return true;
              }
              setParams({ companyId: null, companyDNIS: null });
              return false;
            }}
            getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            renderListOption={(company, liProps) => {
              return (
                <li {...liProps} key={company.id}>
                  <Typography text={company.name} />
                </li>
              );
            }}
            label='Company'
            placeHolder='Company Name'
            sx={{
              marginTop: 1,
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                paddingTop: '3px',
                paddingBottom: '3px'
              },
              '& .MuiFormControl-root.MuiTextField-root': {
                marginY: 0
              }
            }}
          />
        </GridV2>
        {cachedAuthUserQuery.data &&
          (SystemUser({ user: cachedAuthUserQuery.data }) || CallCenterUser({ user: cachedAuthUserQuery.data })) && (
            <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <SearchSelect
                doSearch={async (searchValue) => {
                  const res = await api.accounts.unpagedListNameOnly({
                    params: { searchWords: searchValue, serializer: 'name' }
                  });
                  return res.results;
                }}
                selectedOption={accountQuery?.data || null}
                trySetSelectedOption={async (option) => {
                  if (option) {
                    setParam('accountId', option.id);
                    return true;
                  }
                  deleteParam('accountId');
                  return false;
                }}
                getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                renderListOption={(account, liProps) => {
                  return (
                    <li {...liProps} key={account.id}>
                      <Typography text={account.name} />
                    </li>
                  );
                }}
                label='Account'
                placeHolder='Account Name'
                sx={{
                  marginTop: 1,
                  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                    paddingTop: '3px',
                    paddingBottom: '3px'
                  },
                  '& .MuiFormControl-root.MuiTextField-root': {
                    marginY: 0
                  }
                }}
              />
            </GridV2>
          )}
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Company DNIS'
            initialKey={sanitizedCompanyDNIS}
            options={companyQuery.data?.callCenter.dnisArray.map((dnisItem) => dnisItem.phoneNumber) || []}
            enableEmptyValue
            usePhoneMask
            onChange={(companyDNIS) => setParam('companyDNIS', companyDNIS)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Completion Status'
            initialKey={params.completionStatus}
            options={
              completionStatuses().map((status) => [status, completionStatusDisplay(status)]) as NonEmptyArray<
                [string, string]
              >
            }
            enableEmptyValue
            onChange={(completionStatus) => setParam('completionStatus', completionStatus)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <SelectFilter
            label='Business Transfer Outcome'
            initialKey={params.sipTransferIntent}
            options={
              Object.entries(SipTransferIntentDisplayMap).map(([key, display]) => [key, display]) as NonEmptyArray<
                [string, string]
              >
            }
            enableEmptyValue
            onChange={(sipTransferIntent) => setParam('sipTransferIntent', sipTransferIntent)}
            sx={{ width: '100%' }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
          <DebouncedTextField
            label='Caller ID'
            debounceOptions={{
              initialValue: params.callerId
            }}
            onChangeHandler={(text) => {
              if (text.length !== 10 && text.length !== 0) {
                setCallerIdIsPartiallyFilled(true);
                return;
              }
              setCallerIdIsPartiallyFilled(false);
              setParam('callerId', text);
            }}
            helperText={callerIdIsPartiallyFilled && 'Invalid Phone Ignored'}
            slotProps={{
              input: {
                inputComponent: MaskedPhoneInput,
                endAdornment: params.callerId && (
                  <IconButton
                    size='small'
                    onClick={() => {
                      setCallerIdIsPartiallyFilled(false);
                      deleteParam('callerId');
                    }}>
                    <CloseIcon />
                  </IconButton>
                )
              },
              formHelperText: {
                error: callerIdIsPartiallyFilled
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                paddingRight: 1
              }
            }}
          />
        </GridV2>
        <GridV2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                color='primary'
                checked={params.companySwitched}
                onChange={(e) => setParam('companySwitched', String(!!e.target.checked))}
              />
            }
            label={<Typography text='Company Switched' />}
          />
        </GridV2>
      </GridV2>
      <Divider />
      <Stack
        spacing={2}
        direction={singleColumn ? 'column' : 'row'}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, padding: 1.5 })}>
        <SelectFilter
          label='Date Range'
          initialKey={params.dateRangeFilter}
          options={dateRangeOptions.filter((option) => !(option === DateRangeEnum.YearToDate && isAccountUser))}
          onChange={(dateRangeOption) => {
            if (isDateRangeOption(dateRangeOption)) {
              if (dateRangeOption === DateRangeEnum.Today) {
                setParams({
                  createdAt_gte: createdAt_gte_Today,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.Today
                });
              } else if (dateRangeOption === DateRangeEnum.WeekToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Week,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.WeekToDate
                });
              } else if (dateRangeOption === DateRangeEnum.MonthToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Month,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.MonthToDate
                });
              } else if (dateRangeOption === DateRangeEnum.YearToDate) {
                setParams({
                  createdAt_gte: createdAt_gte_Year,
                  createdAt_lte: createdAt_lte_Today,
                  dateRangeFilter: DateRangeEnum.YearToDate
                });
              }
            }
          }}
          sx={{ width: singleColumn ? '100%' : 'auto' }}
        />
        <DatePicker
          label='Start Date'
          timezone={timezone}
          value={params.createdAt_gte || createdAt_gte_Today}
          onChange={(value) => {
            if (!value) return;
            const startOfSelectedDay = DateUtils.toStartOf(value, 'day', { timezone }).toISOString();
            setParams({
              createdAt_gte: startOfSelectedDay,
              dateRangeFilter: dateRangeOptions[4]
            });
          }}
          textFieldProps={{
            fullWidth: false,
            sx: {
              width: singleColumn ? '100%' : 'auto'
            }
          }}
        />
        <DatePicker
          label='End Date'
          timezone={timezone}
          value={params.createdAt_lte || createdAt_lte_Today}
          onChange={(value) => {
            if (!value) return;
            const endOfSelectedDay = DateUtils.toEndOf(value, 'day', { timezone }).toISOString();
            setParams({
              createdAt_lte: endOfSelectedDay,
              dateRangeFilter: dateRangeOptions[4]
            });
          }}
          textFieldProps={{
            fullWidth: false,
            sx: {
              width: singleColumn ? '100%' : 'auto'
            }
          }}
        />
      </Stack>
      <Divider />
      <Stack
        direction='row-reverse'
        padding={1.5}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default, width: '100%' })}>
        <Button
          color='cancel'
          variant='contained'
          text='Reset Filters'
          disabled={searchParams.toString().length === 0}
          onClick={() => clearParams()}
        />
      </Stack>
      <Divider />
      {aiCallCountQuery.data !== undefined && !!transformListData(aiCallQuery.data).length && (
        <>
          <Stack
            direction='row'
            justifyContent='center'
            spacing={1}
            padding={1}
            sx={(theme) => ({ backgroundColor: theme.palette.background.default })}>
            <Typography text={`Total calls: ${aiCallCountQuery.data}`} variant='body1Bold' />
          </Stack>
          <Divider />
        </>
      )}
      <ScrollableContentWrapper isSavingOrLoading={aiCallQuery.isFetching || companyNameQuery.isFetching}>
        <PagedList
          queryResult={aiCallQuery}
          renderFunction={(aiCall) => {
            const company = companyNameQuery.data?.find((company) => company.id === aiCall.companyId);
            return (
              <ListItem
                key={aiCall.id}
                divider
                disablePadding
                secondaryAction={
                  <Tooltip title='View in new tab' placement='left'>
                    <IconButton
                      onClick={() => {
                        window.open(`${getFullAppRoutePath(appRoutePaths.AiCall)}/${aiCall.id}`, '_blank');
                      }}>
                      {<OpenInNewIcon />}
                    </IconButton>
                  </Tooltip>
                }>
                <LinkListItemButton
                  href={`${getFullAppRoutePath(appRoutePaths.AiCall)}/${aiCall.id}`}
                  onClick={() => {
                    navigate(`${aiCall.id}`, {
                      state: { goBack: getGoBackState(appRoutePaths.AiCall, searchParams) }
                    });
                  }}>
                  <ListItemAvatar>
                    <Avatar>
                      <AiCallIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack spacing={0}>
                        <Typography
                          text={`${aiCall.caller?.name || 'Unknown Caller'} \u2192 ${company?.name || 'Inactive Company'}`}
                        />
                        <Typography
                          variant='body2'
                          text={`${(aiCall.agent && AiCallAgentDisplayMap[aiCall.agent]) || 'Unknown Agent'} \u2014 ${aiCall.completionStatus && completionStatusDisplay(aiCall.completionStatus)}`}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography component='span' variant='inherit' sx={{ display: 'flex', alignItems: 'center' }}>
                        {`${DateUtils.format(aiCall.startedAt, DateFormatEnum.DateTimeWeekdayShort, {
                          timezone
                        })} \u2014 ${aiCall.outcome || 'UNKNOWN'}${aiCall.sipTransferIntent ? ` (${SipTransferIntentDisplayMap[aiCall.sipTransferIntent]})` : ''}`}
                      </Typography>
                    }
                  />
                </LinkListItemButton>
              </ListItem>
            );
          }}
        />
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const AiCallListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();

  const { callCenterCacheResource } = useContext(AuthContext);
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  return (
    <>
      {cachedCallCenterQuery.data?.timezone && (
        <AiCallList appDrawerState={appDrawerState} timezone={cachedCallCenterQuery.data.timezone} />
      )}
    </>
  );
};
